/**
 * ColorControl — Hex color picker with preset swatches.
 */

import { useState, useEffect, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const PRESET_COLORS = [
  '#000000', '#FFFFFF', '#0ea5e9', '#ef4444',
  '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899',
]

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

/** Normalize a user-typed hex string. Returns valid hex or '' if invalid. */
function normalizeHex(raw: string): string {
  if (!raw) return ''
  // Strip duplicate leading hashes
  let s = raw.replace(/^#+/, '#')
  // Add # prefix if missing
  if (!s.startsWith('#')) s = '#' + s
  return HEX_RE.test(s) ? s : ''
}

interface ColorControlProps {
  value: string | undefined
  onChange: (v: string | undefined) => void
  label: string
}

export function ColorControl({ value, onChange, label }: ColorControlProps) {
  const [hex, setHex] = useState(value ?? '')
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const lastValidRef = useRef(value ?? '')

  useEffect(() => {
    setHex(value ?? '')
    lastValidRef.current = value ?? ''
  }, [value])

  /** Instant commit for discrete clicks (swatches, clear button) */
  const commitImmediate = (color: string) => {
    setHex(color)
    clearTimeout(timerRef.current)
    lastValidRef.current = color
    onChange(color || undefined)
  }

  /** Debounced commit for continuous text/picker input */
  const commitDebounced = (color: string) => {
    setHex(color)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      const normalized = normalizeHex(color)
      if (normalized) {
        lastValidRef.current = normalized
        onChange(normalized)
      }
      // Invalid input: don't propagate, wait for blur to revert
    }, 150)
  }

  /** On blur: normalize or revert to last valid value */
  const handleBlur = () => {
    clearTimeout(timerRef.current)
    if (!hex) {
      lastValidRef.current = ''
      onChange(undefined)
      return
    }
    const normalized = normalizeHex(hex)
    if (normalized) {
      setHex(normalized)
      lastValidRef.current = normalized
      onChange(normalized)
    } else {
      // Revert to last known valid value
      setHex(lastValidRef.current)
    }
  }

  useEffect(() => {
    return () => clearTimeout(timerRef.current)
  }, [])

  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-medium text-gray-600">{label}</Label>

      {/* Preset swatches */}
      <div className="flex flex-wrap gap-1.5">
        {PRESET_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => commitImmediate(c)}
            className={`h-7 w-7 rounded-xs border-2 transition-colors ${
              hex.toLowerCase() === c.toLowerCase()
                ? 'border-sky-500'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            style={{ backgroundColor: c }}
            title={c}
          />
        ))}
        {/* Clear button */}
        <button
          type="button"
          onClick={() => commitImmediate('')}
          className={`flex h-7 w-7 items-center justify-center rounded-xs border-2 text-xs text-gray-400 ${
            !hex ? 'border-sky-500' : 'border-gray-200 hover:border-gray-300'
          }`}
          title="None"
        >
          <span className="relative block h-4 w-4">
            <span className="absolute inset-0 rotate-45 border-t border-red-400" />
          </span>
        </button>
      </div>

      {/* Custom hex input */}
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={hex || '#000000'}
          onChange={(e) => commitDebounced(e.target.value)}
          className="h-8 w-8 cursor-pointer rounded-md border border-gray-200 p-0.5"
        />
        <Input
          value={hex}
          onChange={(e) => commitDebounced(e.target.value)}
          onBlur={handleBlur}
          placeholder="#000000"
          className="flex-1"
        />
      </div>
    </div>
  )
}
